import 'dayjs/locale/da';
import 'dayjs/locale/en';
import 'dayjs/locale/fi';
import 'dayjs/locale/nb';
import 'dayjs/locale/sv';

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { daDK, enUS, fiFI, nbNO, svSE } from '@mui/x-date-pickers/locales';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';

import { useAppDispatch, useAppSelector } from '../../hooks';
import useSettings from '../../hooks/useSettings';
import useTranslation from '../../hooks/useTranslation';
import { addOrUpdateFormField } from '../../store/applicationform';
import { selectFieldValue } from '../../store/applicationform/applicationFormSelectors';
import { IFieldState } from '../../store/applicationform/applicationFormTypes';
import { LanguageCode } from '../../types/LanguageCode';
import ValidationError from '../ValidationError/ValidationError';

const DATE_PICKER_LOCALES = {
    [LanguageCode.DA]: { dayjsLocale: 'da', muiLocale: daDK },
    [LanguageCode.EN]: { dayjsLocale: 'en', muiLocale: enUS },
    [LanguageCode.FI]: { dayjsLocale: 'fi', muiLocale: fiFI },
    [LanguageCode.NO]: { dayjsLocale: 'nb', muiLocale: nbNO },
    [LanguageCode.SV]: { dayjsLocale: 'sv', muiLocale: svSE },
};

type Props = {
    name: string;
    label?: string;
    minDate?: Dayjs;
    maxDate?: Dayjs;
};

const CalendarField = ({ name, label, minDate, maxDate = dayjs().endOf('year') }: Props) => {
    const { languageCode } = useSettings();
    const { dayjsLocale, muiLocale } = DATE_PICKER_LOCALES[languageCode] ?? DATE_PICKER_LOCALES[LanguageCode.EN];
    dayjs.locale(dayjsLocale);

    const translate = useTranslation();
    const dispatch = useAppDispatch();
    const value: any = useAppSelector(selectFieldValue)(name);

    const handleChange = (newValue: Dayjs | null) => {
        if (newValue !== null && !newValue.isValid()) {
            return;
        }

        // minDate/maxDate only restrict the calendar UI -- MUI's DatePicker still calls onChange with a
        // free-typed value outside that range, so it must be rejected here too or an out-of-range date
        // would still reach the store.
        const isOutOfRange =
            newValue !== null &&
            ((minDate && newValue.isBefore(minDate, 'month')) || newValue.isAfter(maxDate, 'month'));
        if (isOutOfRange) {
            return;
        }

        dispatch(
            addOrUpdateFormField({
                fieldName: name,
                value: newValue?.format('YYYY-MM'),
                hasError: false,
            } as IFieldState),
        );
    };

    return (
        <>
            <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale={dayjsLocale}
                localeText={muiLocale.components.MuiLocalizationProvider.defaultProps.localeText}>
                <DatePicker
                    views={['month', 'year']}
                    label={label ?? translate('field.applicant-employment-date.label')}
                    value={value ? dayjs(value) : null}
                    onChange={handleChange}
                    minDate={minDate}
                    maxDate={maxDate}
                    slotProps={{
                        textField: {
                            fullWidth: true,
                            sx: { '& .MuiPickersOutlinedInput-root': { borderRadius: '10px' } },
                            slotProps: {
                                inputLabel: { sx: { margin: 0 } },
                            },
                        },
                    }}
                />
            </LocalizationProvider>
            <ValidationError fieldName={name} />
        </>
    );
};

export default CalendarField;
